<script lang="ts" setup>
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import type { User } from "~/models/User";
import { RadioGroup, RadioGroupOption } from "@headlessui/vue";
import type { Brand } from "~/services/BrandService";

definePageMeta({
    title: "Users",
});
const api = useApi();
const dayjs = useDayjs();
const columns = [
    {
        key: "select",
        label: "Select",
    },
    {
        key: "name",
        label: "Name",
    },
    {
        key: "email",
        label: "Email",
    },
    {
        key: "brands",
        label: "Brands",
    },
    {
        key: "created_at",
        label: "Created At",
    },
    {
        key: "joined_at",
        label: "Joined At",
    },
    {
        key: "actions",
    },
];

const {
    items: userItems,
    query,
    page,
    status,
    total,
} = api.users.list({
    query: {
        _mode: "display",
        show: 25,
        per_page: 25,
        sorts: "-updated_at",
        where_has_user: false,
    },
});

const { data: brands } = api.brands.list({
    query: {
        _mode: "display",
        per_page: 25,
        state: undefined,
        sub_state: undefined,
        search: undefined,
        trigger_app: undefined,
        sorts: "-updated_at",
    },
});

const brandList = computed(() => brands.value?.data.data ?? []);
const { users: online } = useAppEcho();
const openBatchInvite = ref<boolean>(false);
const inviting = ref<boolean>(false);
const selectAll = ref<boolean>(false);
const refCheckboxAll = ref(null);
const { user: auth } = useAuth();
const loading = ref<boolean>(true);

const users = computed(() => {
    return (userItems.value as unknown as User[]).map((person: User) => ({
        is_selected: !!selected.value.find((user) => user.id === person.id),
        id: person.id,
        role: person.role,
        roles: person.roles,
        isOnline: online?.value?.find((user) => person.id === user.id),
        name: person.first_name + " " + person.last_name,
        email: person.email,
        brands: person.brands ?? [],
        created_at: person.created_at
            ? dayjs(person.created_at).format("MMMM DD, YYYY")
            : undefined,
        is_invited: person.is_invited,
        accepted_invite: person.accepted_invite,
        joined_at: person.joined_at
            ? dayjs(person.joined_at).format("MMMM DD, YYYY")
            : "-",
        avatar: person.avatar,
        disabled: person.email === auth.value.email,
    }));
});

const selected = ref<User[]>([]);
const isModalInviteOpen = ref<boolean>(false);
const isModalImportOpen = ref<boolean>(false);
const isModalBrandSelectionOpen = ref<boolean>(false);
const table = ref<{ $el: HTMLButtonElement }>();
const selectedBrand = ref<Brand | undefined>(undefined);
const selectedBrandError = reactive({
    hasLinkedin: true,
    hasDomainOrUrl: true,
});

watch(brandList, (newBrandList: Brand[]) => {
    if (newBrandList.length > 0 && !selectedBrand.value) {
        selectedBrand.value = newBrandList[0] as Brand;
    }
}, { immediate: true });

watch(status, (to) => {
    if (to === "success") {
        selectAll.value = false;
        selected.value = [];
    }
});

const invitePeople = (users: User[]) => {
    inviting.value = true;
    Promise.allSettled(
        users.map((person: User) =>
            api.users.invite({
                email: person.email as string,
            }),
        ),
    ).finally(() => {
        let title = `${selected.value.length} users have been invited.`;
        if (selected.value.length === 1) {
            title = `${selected.value.length} user has been invited.`;
        }
        useToast().add({
            title,
        });
        onSelectAll(false);
        inviting.value = false;
        openBatchInvite.value = false;
        refreshNuxtData();
    });
};

watchOnce(table, (to) => {
    if (to && to.$el) {
        to.$el.addEventListener("click", (e) => {
            if (e.target && !e.target.closest("a,button")) {
                const td = e.target.closest("td");

                if (!td || td.querySelector("input")) return;

                const btn = e.target
                    .closest("tr")
                    ?.querySelector(".detail-button");

                btn.click();
            }
        });
    }
});

// on user click checkbox at row
const selectRow = (row: User) => {
    if (selected.value.find((user) => user.id === row.id)) {
        selected.value = selected.value.filter((user) => user.id !== row.id);
    } else {
        selected.value.push(row);
    }

    selected.value = selected.value.filter((user) => user.email !== auth.value.email);
    selectAll.value = selected.value.length === userItems.value.filter((user) => (user as unknown as User).email !== auth.value.email).length;
};

// on user click checkbox at header
const onSelectAll = (value: boolean) => {
    selected.value = value ? (userItems.value as unknown as User[]) : [];
    selected.value = selected.value.filter((user) => user.email !== auth.value.email);
};

const openModalImport = () => {
    if (!brandList.value.length) {
        return;
    }
    
    if (brandList.value.length === 1) {
        selectedBrand.value = brandList.value[0] as Brand;
        isModalImportOpen.value = true;
    } else {
        isModalBrandSelectionOpen.value = true;
    }
};

watch(
    () => selectedBrand.value,
    (brand: Brand) => {
        selectedBrandError.hasDomainOrUrl = true;
        selectedBrandError.hasLinkedin = true;
        if (!brand.domain && !brand.metadata.website) {
            selectedBrandError.hasDomainOrUrl = false;
        }

        if (!brand.metadata.socials.linked_in) {
            selectedBrandError.hasLinkedin = false;
        }
    },
);
</script>

<template>
    <CreateUser v-model="isModalInviteOpen" />
    <AppSettingsContent class="flex-1 flex flex-col h-full" description="Manage your users" title="Users">
        <template #action>
            <div class="flex-1 flex justify-end items-end h-full">
                <div class="flex items-center gap-4">
                    <template v-if="selected.length">
                        <UButton color="primary" size="md" @click="openBatchInvite = true">
                            <template #leading>
                                <FontAwesomeIcon :icon="faUserPlus" />
                            </template>
                            Invite
                        </UButton>
                    </template>
                    <template v-else>
                        <UButton size="md" @click="isModalInviteOpen = true">
                            <template #leading>
                                <FontAwesomeIcon :icon="faPlus" />
                            </template>
                            Add User
                        </UButton>
                    </template>
                    <UButton v-if="brandList.length" size="md" @click="openModalImport">
                        <template #leading>
                            <FontAwesomeIcon :icon="faLinkedin" />
                        </template>
                        Import via LinkedIn
                    </UButton>
                    <UInput v-model="query.search" class="w-80" placeholder="Search users..." size="lg" />
                </div>
            </div>
        </template>
        <UITable ref="table" :columns="columns" :rows="users" :loading="loading">
            <template #select-header>
                <UCheckbox ref="refCheckboxAll" v-model="selectAll" @change="onSelectAll" />
            </template>
            <template #select-data="{ row }">
                <UCheckbox :model-value="row.is_selected" :disabled="row.disabled" @change="selectRow(row)" />
            </template>
            <template #name-data="{ row }">
                <UButton :to="{
                    name: 'settings-account-users-id',
                    params: { id: row.id },
                }" :padded="false" variant="ghost" color="white" class="detail-button flex-row flex items-center">
                    <UChip :color="row.isOnline ? 'green' : 'gray'" class="mr-4" inset position="bottom-right"
                        size="lg">
                        <UAvatar :alt="row.name" :src="row.avatar" size="md" />
                    </UChip>
                    <span class="flex flex-col gap-0">
                        <span>{{ row.name }}</span>
                        <div class="flex flex-wrap gap-1 items-center">
                            <template v-for="(role, index) in row.roles" :key="role">
                                <span v-if="index > 0" class="text-xs opacity-25">•</span>
                                <div class="text-xs opacity-50">
                                    {{ $t(`labels.${role}`) }}
                                </div>
                            </template>
                        </div>
                    </span>
                </UButton>
            </template>
            <template #brands-data="{ row }">
                <div v-if="row.brands.length">
                    <span v-for="brand in row.brands" :key="brand.id"
                        class="inline-flex gap-1 items-center rounded-full bg-gray-50 px-1.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                        <UAvatar :alt="brand.name" :src="brand.logo" size="2xs" />{{ brand.name }}
                    </span>
                </div>
                <div v-else>-</div>
            </template>
            <template #actions-data="{ row }">
                <div v-if="!row.disabled" class="flex justify-start items-center">
                    <DropdownPeople :user="row" />
                </div>
            </template>
        </UITable>
        <template #bottom>
            <LazyAppListPaginationFooter v-model:page="page" v-model:show="query.per_page"
                :options="[5, 10, 15, 20, 25, 50, 100]" :show="query?.per_page ?? 25" :total="total" label="people" />
        </template>
    </AppSettingsContent>
    <UIModalBulkInvite v-model:open="openBatchInvite" :inviting="inviting" :items="selected" :object="['user', 'users']"
        by="name" @invite="invitePeople(selected)" />
    <UModal v-model="isModalBrandSelectionOpen" :ui="{
        width: 'w-full sm:max-w-lg',
    }">
        <div class="p-8 flex flex-col gap-6">
            <div class="sm:flex-auto">
                <h1 class="text-base font-semibold leading-6 text-gray-900">
                    Select brand
                </h1>
                <p class="mt-2 text-sm text-gray-700">
                    Please select the brand you want to import users from.
                </p>
            </div>
            <fieldset aria-label="Server size">
                <RadioGroup v-model="selectedBrand" class="space-y-4">
                    <RadioGroupOption v-for="brand in brandList" :key="brand.id" v-slot="{ active, checked }"
                        as="template" :value="brand" :aria-label="brand.name">
                        <div :class="[
                            active
                                ? 'border-hd-secondary-400 ring-2 ring-hd-secondary-400'
                                : 'border-gray-300',
                            'relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between',
                        ]">
                            <span class="flex items-center gap-4">
                                <UAvatar :alt="brand.name" :src="brand.avatar" size="md" />
                                <span class="flex flex-col gap-0 text-black font-medium">
                                    <span>{{ brand.name }}</span>
                                </span>
                            </span>
                            <span v-if="brand.id === auth?.account?.brand?.id"
                                class="flex text-sm sm:ml-4 sm:text-right items-center">
                                <span class="ml-1 text-gray-500 sm:ml-0">Default</span>
                            </span>
                            <span :class="[
                                active ? 'border' : 'border-2',
                                checked
                                    ? 'border-hd-secondary-400'
                                    : 'border-transparent',
                                'pointer-events-none absolute -inset-px rounded-lg',
                            ]" aria-hidden="true" />
                        </div>
                    </RadioGroupOption>
                </RadioGroup>
            </fieldset>

            <div v-if="
                !selectedBrandError.hasDomainOrUrl ||
                !selectedBrandError.hasLinkedin
            " class="rounded-md bg-red-50 p-4">
                <div class="flex">
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-red-800">
                            The
                            <NuxtLink :to="{
                                name: 'settings-account-brands-id',
                                params: { id: selectedBrand.id },
                            }" class="detail-button hover:underline">
                                <span class="font-medium text-hd-secondary-600">{{ selectedBrand.name }}</span>
                            </NuxtLink>
                            should have this information
                        </h3>
                        <div class="mt-2 text-sm text-red-700">
                            <ul role="list" class="list-disc space-y-1 pl-5">
                                <li v-if="!selectedBrandError.hasLinkedin">
                                    Brand must have LinkedIn social filled
                                </li>
                                <li v-if="!selectedBrandError.hasDomainOrUrl">
                                    Brand must have a domain or website URL
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end gap-2">
                <UButton color="white" @click="isModalBrandSelectionOpen = false">
                    Cancel
                </UButton>
                <UButton :disabled="!selectedBrandError.hasDomainOrUrl ||
                    !selectedBrandError.hasLinkedin ||
                    !selectedBrand
                    " @click="
                        {
                        isModalBrandSelectionOpen = false;
                        isModalImportOpen = true;
                    }
                        ">Fetch users</UButton>
            </div>
        </div>
    </UModal>
    <UIModalImportUsers v-model:open="isModalImportOpen" :selected-brand="selectedBrand" />
</template>
